import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, MoreVertical, Users, AlertCircle, Loader2, Trash2, X, Plus, Phone, Mail, MapPin, Calendar, Edit2, Lightbulb, ArrowRight, Flame, ThermometerSun, Snowflake, CheckCircle2, Clock, UserCircle, Upload, Download, History, MessageSquare, PhoneCall, MailIcon, CalendarCheck, ArrowUpDown, UserPlus, CalendarPlus, ExternalLink, HelpCircle, Flag, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth, useUser } from '@clerk/clerk-react';
import Papa from 'papaparse';
import { ViewToggle, LeadsPipeline, StageRequirementsModal, LeadViewModal, ValidationResult, LeadStatus } from '../components/leads';

interface MarketingUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  role: string;
}

interface ImportPreviewRow {
  contact_name: string;
  studio_name?: string;
  email?: string;
  phone?: string;
  location?: string;
  source?: string;
  pain_point?: string;
  current_software?: string;
  score?: string;
  status?: string;
  notes?: string;
  isValid: boolean;
  errors: string[];
}

interface LeadActivity {
  id: string;
  lead_id: string;
  activity_type: 'note' | 'call' | 'email' | 'meeting' | 'status_change' | 'score_change' | 'assignment_change';
  old_value: string | null;
  new_value: string | null;
  content: string | null;
  created_by: string;
  created_by_name?: string;
  created_at: string;
}

interface Lead {
  id: string;
  contact_name: string;
  studio_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  source: string | null;
  pain_point: string | null;
  current_software: string | null;
  score: 'hot' | 'warm' | 'cold';
  status: 'new' | 'contacted' | 'demo_scheduled' | 'demo_done' | 'converted' | 'lost';
  is_qualified: number;
  is_decision_maker: number;
  demo_scheduled: number;
  demo_date: string | null;
  demo_time: string | null;
  notes: string | null;
  last_contact_date: string | null;
  next_follow_up_date: string | null;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
}

interface LeadStats {
  total: number;
  hot: number;
  warm: number;
  cold: number;
  new_leads: number;
  contacted: number;
  demo_scheduled: number;
  demo_done: number;
  converted: number;
  lost: number;
  due_follow_ups: number;
}

const emptyStats: LeadStats = {
  total: 0,
  hot: 0,
  warm: 0,
  cold: 0,
  new_leads: 0,
  contacted: 0,
  demo_scheduled: 0,
  demo_done: 0,
  converted: 0,
  lost: 0,
  due_follow_ups: 0,
};

const scoreColors = {
  hot: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800',
  warm: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800',
  cold: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800',
};

const statusLabels: Record<string, string> = {
  new: 'New',
  contacted: 'Contacted',
  demo_scheduled: 'Demo Scheduled',
  demo_done: 'Demo Done',
  converted: 'Converted',
  lost: 'Lost',
};

const statusColors: Record<string, string> = {
  new: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
  contacted: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
  demo_scheduled: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
  demo_done: 'bg-sage/10 text-sage',
  converted: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
  lost: 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400',
};

const sourceLabels: Record<string, string> = {
  reddit: 'Reddit',
  instagram: 'Instagram',
  google: 'Google',
  referral: 'Referral',
  cold_outreach: 'Cold Outreach',
  other: 'Other',
};

export default function LeadsPage() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const isAdmin = (user?.publicMetadata?.role as string) === 'admin';
  const [leads, setLeads] = useState<Lead[]>([]);
  const [stats, setStats] = useState<LeadStats>(emptyStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterScore, setFilterScore] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'updated_at' | 'created_at' | 'contact_name'>('updated_at');
  const [currentPage, setCurrentPage] = useState(1);
  const leadsPerPage = 25;
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [expandedLeadId, setExpandedLeadId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [deleteConfirmLead, setDeleteConfirmLead] = useState<Lead | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [marketingUsers, setMarketingUsers] = useState<MarketingUser[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState<ImportPreviewRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityLead, setActivityLead] = useState<Lead | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingLead, setViewingLead] = useState<Lead | null>(null);
  const [activities, setActivities] = useState<LeadActivity[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [addingActivity, setAddingActivity] = useState(false);
  const [newActivityType, setNewActivityType] = useState<'note' | 'call' | 'email' | 'meeting'>('note');
  const [newActivityContent, setNewActivityContent] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    contact_name: '',
    studio_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    source: '',
    pain_point: '',
    current_software: '',
    score: 'cold' as Lead['score'],
    status: 'new' as Lead['status'],
    is_qualified: false,
    is_decision_maker: false,
    demo_scheduled: false,
    demo_date: '',
    demo_time: '',
    notes: '',
    next_follow_up_date: '',
    assigned_to: '' as string,
  });

  const [showTips, setShowTips] = useState(() => {
    return localStorage.getItem('leads_tips_dismissed') !== 'true';
  });

  const [viewMode, setViewMode] = useState<'pipeline' | 'list'>(() => {
    return (localStorage.getItem('leads_view_mode') as 'pipeline' | 'list') || 'pipeline';
  });

  // Stage validation modal state
  const [showRequirementsModal, setShowRequirementsModal] = useState(false);
  const [validationLead, setValidationLead] = useState<Lead | null>(null);
  const [validationTarget, setValidationTarget] = useState<LeadStatus | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [leadActivityCounts, setLeadActivityCounts] = useState<Record<string, { total: number; hasMeeting: boolean }>>({});

  const handleViewModeChange = (mode: 'pipeline' | 'list') => {
    setViewMode(mode);
    localStorage.setItem('leads_view_mode', mode);
  };

  const toggleTips = () => {
    const newValue = !showTips;
    setShowTips(newValue);
    localStorage.setItem('leads_tips_dismissed', newValue ? 'false' : 'true');
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();

      const params = new URLSearchParams();
      if (filterScore) params.append('score', filterScore);
      if (filterStatus) params.append('status', filterStatus);
      if (searchQuery) params.append('search', searchQuery);

      const response = await fetch(`${apiUrl}/admin/leads?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch leads');
      }
      const data = await response.json();
      setLeads(data.leads || []);
      setStats(data.stats || emptyStats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load leads');
      setLeads([]);
      setStats(emptyStats);
    } finally {
      setLoading(false);
    }
  }, [getToken, filterScore, filterStatus, searchQuery]);

  // Fetch marketing users for assignment dropdown
  const fetchMarketingUsers = useCallback(async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        // Filter to only marketing and admin users who can be assigned leads
        const assignableUsers = (data.users || []).filter(
          (u: MarketingUser) => u.role === 'marketing' || u.role === 'admin'
        );
        setMarketingUsers(assignableUsers);
      }
    } catch (err) {
      console.error('Failed to fetch marketing users:', err);
    }
  }, [getToken]);

  // Fetch activity counts for all leads (for validation)
  const fetchActivityCounts = useCallback(async (_leadsList: Lead[]) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/leads/activity-counts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setLeadActivityCounts(data.counts || {});
      }
    } catch (err) {
      console.error('Failed to fetch activity counts:', err);
    }
  }, [getToken]);

  useEffect(() => {
    fetchLeads();
    fetchMarketingUsers();
  }, [fetchLeads, fetchMarketingUsers]);

  // Fetch activity counts when leads change
  useEffect(() => {
    if (leads.length > 0) {
      fetchActivityCounts(leads);
    }
  }, [leads, fetchActivityCounts]);

  const resetForm = () => {
    setFormData({
      contact_name: '',
      studio_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      country: '',
      source: '',
      pain_point: '',
      current_software: '',
      score: 'cold' as Lead['score'],
      status: 'new' as Lead['status'],
      is_qualified: false,
      is_decision_maker: false,
      demo_scheduled: false,
      demo_date: '',
      demo_time: '',
      notes: '',
      next_follow_up_date: '',
      assigned_to: '',
    });
  };

  // Helper to get user display name
  const getUserDisplayName = (userId: string | null) => {
    if (!userId) return null;
    const user = marketingUsers.find(u => u.id === userId);
    if (!user) return null;
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    // Return only the part before @ for email
    return user.email?.split('@')[0] || null;
  };

  const handleAddLead = async () => {
    if (!formData.contact_name.trim()) {
      setError('Contact name is required');
      return;
    }

    setSaving(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/leads`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create lead');
      }

      const data = await response.json();
      setLeads(prev => [data.lead, ...prev]);
      setShowAddModal(false);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  const handleEditLead = async () => {
    if (!editingLead) return;

    setSaving(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/leads/${editingLead.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update lead');
      }

      const data = await response.json();
      setLeads(prev => prev.map(l => l.id === editingLead.id ? data.lead : l));
      setShowEditModal(false);
      setEditingLead(null);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLead = async (lead: Lead) => {
    setDeleting(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/leads/${lead.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete lead');
      }

      setLeads(prev => prev.filter(l => l.id !== lead.id));
      setDeleteConfirmLead(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete lead');
    } finally {
      setDeleting(false);
    }
  };

  const handleQuickStatusChange = async (lead: Lead, newStatus: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/leads/${lead.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) throw new Error('Failed to update status');

      const data = await response.json();
      setLeads(prev => prev.map(l => l.id === lead.id ? data.lead : l));
      setOpenMenuId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  // Handle validation failure from pipeline drag
  const handleValidationFailed = (lead: Lead, targetStatus: LeadStatus, validation: ValidationResult) => {
    setValidationLead(lead);
    setValidationTarget(targetStatus);
    setValidationResult(validation);
    setShowRequirementsModal(true);
  };

  // Handle save and move from requirements modal
  const handleSaveAndMove = async (
    updates: Partial<Lead>,
    addActivity?: { type: 'call' | 'email' | 'meeting' | 'note'; content: string }
  ) => {
    if (!validationLead || !validationTarget) return;

    const lead = validationLead;
    const targetStatus = validationTarget;

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();

      // First, add activity if needed
      if (addActivity) {
        await fetch(`${apiUrl}/admin/leads/${lead.id}/activities`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            activity_type: addActivity.type,
            content: addActivity.content,
          }),
        });

        // Update local activity counts
        setLeadActivityCounts(prev => ({
          ...prev,
          [lead.id]: {
            total: (prev[lead.id]?.total || 0) + 1,
            hasMeeting: prev[lead.id]?.hasMeeting || addActivity.type === 'meeting',
          },
        }));
      }

      // Then update the lead with new fields and status
      const response = await fetch(`${apiUrl}/admin/leads/${lead.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...updates, status: targetStatus }),
      });

      if (!response.ok) throw new Error('Failed to update lead');

      const data = await response.json();
      setLeads(prev => prev.map(l => l.id === lead.id ? data.lead : l));

      setShowRequirementsModal(false);
      setValidationLead(null);
      setValidationTarget(null);
      setValidationResult(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update lead');
    }
  };

  // Open view-only modal
  const openViewModal = (lead: Lead) => {
    setViewingLead(lead);
    setShowViewModal(true);
  };

  const openEditModal = (lead: Lead) => {
    setEditingLead(lead);
    setFormData({
      contact_name: lead.contact_name,
      studio_name: lead.studio_name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      address: lead.address || '',
      city: lead.city || '',
      state: lead.state || '',
      country: lead.country || '',
      source: lead.source || '',
      pain_point: lead.pain_point || '',
      current_software: lead.current_software || '',
      score: lead.score,
      status: lead.status,
      is_qualified: Boolean(lead.is_qualified),
      is_decision_maker: Boolean(lead.is_decision_maker),
      demo_scheduled: Boolean(lead.demo_scheduled),
      demo_date: lead.demo_date || '',
      demo_time: lead.demo_time || '',
      notes: lead.notes || '',
      next_follow_up_date: lead.next_follow_up_date || '',
      assigned_to: lead.assigned_to || '',
    });
    setShowEditModal(true);
  };

  const filteredLeads = leads
    .filter(lead => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          lead.contact_name.toLowerCase().includes(query) ||
          (lead.studio_name && lead.studio_name.toLowerCase().includes(query)) ||
          (lead.email && lead.email.toLowerCase().includes(query)) ||
          (lead.city && lead.city.toLowerCase().includes(query)) ||
          (lead.country && lead.country.toLowerCase().includes(query));
        if (!matchesSearch) return false;
      }
      if (filterScore && lead.score !== filterScore) return false;
      if (filterStatus && lead.status !== filterStatus) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'updated_at') {
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      } else if (sortBy === 'created_at') {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      } else {
        return a.contact_name.localeCompare(b.contact_name);
      }
    });

  // Pagination calculations
  const totalPages = Math.ceil(filteredLeads.length / leadsPerPage);
  const paginatedLeads = filteredLeads.slice(
    (currentPage - 1) * leadsPerPage,
    currentPage * leadsPerPage
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterScore, filterStatus, sortBy]);

  const isOverdue = (date: string | null) => {
    if (!date) return false;
    return new Date(date) <= new Date();
  };

  // CSV Import handlers
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const validScores = ['hot', 'warm', 'cold'];
        const validStatuses = ['new', 'contacted', 'demo_scheduled', 'demo_done', 'converted', 'lost'];

        const preview: ImportPreviewRow[] = (results.data as Record<string, string>[]).map((row) => {
          const errors: string[] = [];

          // Required field validation
          if (!row.contact_name?.trim()) {
            errors.push('Contact name is required');
          }

          // Email format validation (if provided)
          if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
            errors.push('Invalid email format');
          }

          // Score validation
          const score = row.score?.toLowerCase();
          if (score && !validScores.includes(score)) {
            errors.push(`Invalid score: ${row.score}. Use hot, warm, or cold`);
          }

          // Status validation
          const status = row.status?.toLowerCase();
          if (status && !validStatuses.includes(status)) {
            errors.push(`Invalid status: ${row.status}`);
          }

          return {
            contact_name: row.contact_name || '',
            studio_name: row.studio_name || '',
            email: row.email || '',
            phone: row.phone || '',
            location: row.location || '',
            source: row.source || '',
            pain_point: row.pain_point || '',
            current_software: row.current_software || '',
            score: score || 'cold',
            status: status || 'new',
            notes: row.notes || '',
            isValid: errors.length === 0,
            errors,
          };
        });

        setImportPreview(preview);
        setShowImportModal(true);
      },
      error: (err) => {
        setError(`Failed to parse CSV: ${err.message}`);
      },
    });

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImportLeads = async () => {
    const validLeads = importPreview.filter(row => row.isValid);
    if (validLeads.length === 0) {
      setError('No valid leads to import');
      return;
    }

    setImporting(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();

      const leadsToImport = validLeads.map(row => ({
        contact_name: row.contact_name,
        studio_name: row.studio_name || null,
        email: row.email || null,
        phone: row.phone || null,
        location: row.location || null,
        source: row.source || null,
        pain_point: row.pain_point || null,
        current_software: row.current_software || null,
        score: row.score || 'cold',
        status: row.status || 'new',
        notes: row.notes || null,
      }));

      const response = await fetch(`${apiUrl}/admin/leads/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ leads: leadsToImport }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to import leads');
      }

      const data = await response.json();
      setShowImportModal(false);
      setImportPreview([]);
      fetchLeads(); // Refresh the list
      console.log(`Successfully imported ${data.imported} leads`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import leads');
    } finally {
      setImporting(false);
    }
  };

  // CSV Export handler
  const handleExportLeads = () => {
    const dataToExport = filteredLeads.map(lead => ({
      contact_name: lead.contact_name,
      studio_name: lead.studio_name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      address: lead.address || '',
      city: lead.city || '',
      state: lead.state || '',
      country: lead.country || '',
      source: lead.source || '',
      pain_point: lead.pain_point || '',
      current_software: lead.current_software || '',
      score: lead.score,
      status: lead.status,
      notes: lead.notes || '',
      demo_date: lead.demo_date || '',
      demo_time: lead.demo_time || '',
      next_follow_up_date: lead.next_follow_up_date || '',
      created_at: lead.created_at,
    }));

    const csv = Papa.unparse(dataToExport);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Activity Timeline handlers
  const openActivityModal = async (lead: Lead) => {
    setActivityLead(lead);
    setShowActivityModal(true);
    setNewActivityType('note');
    setNewActivityContent('');
    await fetchActivities(lead.id);
  };

  const fetchActivities = async (leadId: string) => {
    setLoadingActivities(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/leads/${leadId}/activities`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setActivities(data.activities || []);
      }
    } catch (err) {
      console.error('Failed to fetch activities:', err);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleAddActivity = async () => {
    if (!activityLead || !newActivityContent.trim()) return;

    setAddingActivity(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/leads/${activityLead.id}/activities`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          activity_type: newActivityType,
          content: newActivityContent,
        }),
      });

      if (response.ok) {
        setNewActivityContent('');
        await fetchActivities(activityLead.id);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to add activity');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add activity');
    } finally {
      setAddingActivity(false);
    }
  };

  // Activity type icons and labels
  const activityTypeConfig: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
    note: { icon: <MessageSquare className="w-4 h-4" />, label: 'Note', color: 'bg-blue-500' },
    call: { icon: <PhoneCall className="w-4 h-4" />, label: 'Call', color: 'bg-green-500' },
    email: { icon: <MailIcon className="w-4 h-4" />, label: 'Email', color: 'bg-purple-500' },
    meeting: { icon: <CalendarCheck className="w-4 h-4" />, label: 'Meeting', color: 'bg-amber-500' },
    status_change: { icon: <ArrowUpDown className="w-4 h-4" />, label: 'Status Change', color: 'bg-stone-500' },
    score_change: { icon: <Flame className="w-4 h-4" />, label: 'Score Change', color: 'bg-orange-500' },
    assignment_change: { icon: <UserPlus className="w-4 h-4" />, label: 'Assignment', color: 'bg-cyan-500' },
  };

  const formatActivityDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Check if a status change is going backwards (out of order)
  const isOutOfOrderStatusChange = (oldStatus: string | null, newStatus: string | null): boolean => {
    if (!oldStatus || !newStatus) return false;

    // Define the expected order (excluding 'lost' as it can happen anytime)
    const statusOrder = ['new', 'contacted', 'demo_scheduled', 'demo_done', 'converted'];

    const oldIndex = statusOrder.indexOf(oldStatus);
    const newIndex = statusOrder.indexOf(newStatus);

    // If either status is not in the order (like 'lost'), it's not out of order
    if (oldIndex === -1 || newIndex === -1) return false;

    // It's out of order if moving backwards (new index < old index)
    return newIndex < oldIndex;
  };

  // Calendar Integration helpers
  const formatICSDate = (dateStr: string, timeStr: string, addMinutes: number = 0) => {
    // Parse date (YYYY-MM-DD) and time (HH:MM)
    const [year, month, day] = dateStr.split('-').map(Number);
    const [hours, minutes] = (timeStr || '09:00').split(':').map(Number);

    const date = new Date(year, month - 1, day, hours, minutes + addMinutes);

    // Format as ICS datetime: YYYYMMDDTHHMMSS
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(date.getHours())}${pad(date.getMinutes())}00`;
  };

  const generateICSFile = (lead: Lead) => {
    if (!lead.demo_date) return;

    const startDate = formatICSDate(lead.demo_date, lead.demo_time || '09:00');
    const endDate = formatICSDate(lead.demo_date, lead.demo_time || '09:00', 30); // 30 min duration
    const now = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const uid = `${lead.id}@serenetouch.app`;

    const description = [
      `Demo with ${lead.contact_name}`,
      lead.studio_name ? `Studio: ${lead.studio_name}` : '',
      lead.email ? `Email: ${lead.email}` : '',
      lead.phone ? `Phone: ${lead.phone}` : '',
      lead.pain_point ? `Pain Point: ${lead.pain_point}` : '',
      lead.notes ? `Notes: ${lead.notes}` : '',
    ].filter(Boolean).join('\\n');

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//SereneTouch//Lead Demo//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${now}`,
      `DTSTART:${startDate}`,
      `DTEND:${endDate}`,
      `SUMMARY:Demo - ${lead.studio_name || lead.contact_name}`,
      `DESCRIPTION:${description}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `demo-${lead.studio_name || lead.contact_name}-${lead.demo_date}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generateGoogleCalendarUrl = (lead: Lead) => {
    if (!lead.demo_date) return '';

    const startDate = formatICSDate(lead.demo_date, lead.demo_time || '09:00');
    const endDate = formatICSDate(lead.demo_date, lead.demo_time || '09:00', 30);

    const details = [
      `Demo with ${lead.contact_name}`,
      lead.studio_name ? `Studio: ${lead.studio_name}` : '',
      lead.email ? `Email: ${lead.email}` : '',
      lead.phone ? `Phone: ${lead.phone}` : '',
      lead.pain_point ? `Pain Point: ${lead.pain_point}` : '',
    ].filter(Boolean).join('\n');

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: `Demo - ${lead.studio_name || lead.contact_name}`,
      dates: `${startDate}/${endDate}`,
      details,
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  return (
    <div className="space-y-6 w-full max-w-full">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-red-700 dark:text-red-300">Error</p>
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Tips Banner */}
      {showTips && (
        <div className="bg-gradient-to-r from-sage/10 to-sage/5 dark:from-sage/20 dark:to-sage/10 border border-sage/30 dark:border-sage/40 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-sage/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Lightbulb className="w-4 h-4 text-sage" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-2">
                <h3 className="font-medium text-stone-800 dark:text-stone-100">Quick Guide: Managing Leads</h3>
                <button
                  onClick={toggleTips}
                  className="p-1 rounded-lg hover:bg-sage/20 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-sage/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-sage">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-stone-700 dark:text-stone-300">Add a Lead</p>
                    <p className="text-stone-500 dark:text-stone-400">Click "Add Lead" to capture a new potential customer</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-sage/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-sage">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-stone-700 dark:text-stone-300">Set Score</p>
                    <p className="text-stone-500 dark:text-stone-400">
                      <span className="inline-flex items-center gap-1"><Flame className="w-3 h-3 text-red-500" />Hot</span> = ready to buy,{' '}
                      <span className="inline-flex items-center gap-1"><ThermometerSun className="w-3 h-3 text-amber-500" />Warm</span> = interested,{' '}
                      <span className="inline-flex items-center gap-1"><Snowflake className="w-3 h-3 text-blue-500" />Cold</span> = early stage
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-sage/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-sage">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-stone-700 dark:text-stone-300">Track Progress</p>
                    <p className="text-stone-500 dark:text-stone-400">Update status: New <ArrowRight className="w-3 h-3 inline" /> Contacted <ArrowRight className="w-3 h-3 inline" /> Demo <ArrowRight className="w-3 h-3 inline" /> Converted</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-sage/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-sage">4</span>
                  </div>
                  <div>
                    <p className="font-medium text-stone-700 dark:text-stone-300">Follow Up</p>
                    <p className="text-stone-500 dark:text-stone-400">Set follow-up dates to stay on top of leads. Overdue items are highlighted.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/20 dark:bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-stone-900 rounded-xl p-6 flex items-center gap-3 shadow-xl">
            <Loader2 className="w-6 h-6 text-sage animate-spin" />
            <span className="text-stone-700 dark:text-stone-300">Loading leads...</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-stone-800 dark:text-stone-100">Leads</h1>
          <p className="text-sm sm:text-base text-stone-500 dark:text-stone-400 mt-1">
            Track and manage potential studio customers
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Help button to toggle tips */}
          <button
            onClick={toggleTips}
            className={`flex items-center justify-center p-2 border rounded-xl font-medium transition-colors ${
              showTips
                ? 'border-sage bg-sage/10 text-sage'
                : 'border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800'
            }`}
            title={showTips ? 'Hide tips' : 'Show tips'}
          >
            <HelpCircle className="w-4 h-4" />
          </button>
          {/* Hidden file input for import */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center justify-center gap-2 px-3 py-2 border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 rounded-xl font-medium hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
            title="Import leads from CSV"
          >
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">Import</span>
          </button>
          <button
            onClick={handleExportLeads}
            disabled={filteredLeads.length === 0}
            className="flex items-center justify-center gap-2 px-3 py-2 border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 rounded-xl font-medium hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={`Export ${filteredLeads.length} leads to CSV`}
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-sage text-white rounded-xl font-medium hover:bg-sage/90 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>Add Lead</span>
          </button>
        </div>
      </div>

      {/* Stats - 3x2 grid on mobile, 6 columns on desktop */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-2">
          <p className="text-base font-bold text-stone-800 dark:text-stone-100">{stats.total}</p>
          <p className="text-xs text-stone-500">Total</p>
        </div>
        <div className={`rounded-xl border p-2 ${scoreColors.hot}`}>
          <p className="text-base font-bold">{stats.hot}</p>
          <p className="text-xs opacity-80">Hot</p>
        </div>
        <div className={`rounded-xl border p-2 ${scoreColors.warm}`}>
          <p className="text-base font-bold">{stats.warm}</p>
          <p className="text-xs opacity-80">Warm</p>
        </div>
        <div className={`rounded-xl border p-2 ${scoreColors.cold}`}>
          <p className="text-base font-bold">{stats.cold}</p>
          <p className="text-xs opacity-80">Cold</p>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 p-2">
          <p className="text-base font-bold text-green-600 dark:text-green-400">{stats.converted}</p>
          <p className="text-xs text-green-600 dark:text-green-400 opacity-80">Converted</p>
        </div>
        <div className={`rounded-xl border p-2 ${stats.due_follow_ups > 0 ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-800'}`}>
          <p className={`text-base font-bold ${stats.due_follow_ups > 0 ? 'text-red-600 dark:text-red-400' : 'text-stone-800 dark:text-stone-100'}`}>{stats.due_follow_ups}</p>
          <p className={`text-xs ${stats.due_follow_ups > 0 ? 'text-red-600 dark:text-red-400' : 'text-stone-500'}`}>Follow-ups</p>
        </div>
      </div>

      {/* Search, Filters, and View Toggle */}
      <div className="flex flex-col gap-3">
        {/* Search bar - full width on all screens */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <input
            type="text"
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-xl text-sm text-stone-700 dark:text-stone-300 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
          />
        </div>
        {/* Filters and View Toggle */}
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={filterScore || ''}
            onChange={(e) => setFilterScore(e.target.value || null)}
            className="flex-1 min-w-[100px] max-w-[140px] px-3 py-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-xl text-sm text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
          >
            <option value="">All Scores</option>
            <option value="hot">Hot</option>
            <option value="warm">Warm</option>
            <option value="cold">Cold</option>
          </select>
          {viewMode === 'list' && (
            <select
              value={filterStatus || ''}
              onChange={(e) => setFilterStatus(e.target.value || null)}
              className="flex-1 min-w-[120px] max-w-[160px] px-3 py-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-xl text-sm text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
            >
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="demo_scheduled">Demo Scheduled</option>
              <option value="demo_done">Demo Done</option>
              <option value="converted">Converted</option>
              <option value="lost">Lost</option>
            </select>
          )}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'updated_at' | 'created_at' | 'contact_name')}
            className="flex-1 min-w-[130px] max-w-[160px] px-3 py-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-xl text-sm text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
          >
            <option value="updated_at">Last Updated</option>
            <option value="created_at">Date Created</option>
            <option value="contact_name">Name A-Z</option>
          </select>
          <div className="ml-auto">
            <ViewToggle view={viewMode} onChange={handleViewModeChange} />
          </div>
        </div>
      </div>

      {/* Pipeline View */}
      {viewMode === 'pipeline' && (
        <div className="leads-pipeline-scroll">
          <LeadsPipeline
            leads={filteredLeads}
            activityCounts={leadActivityCounts}
            onStatusChange={(lead, newStatus) => handleQuickStatusChange(lead, newStatus)}
            onValidationFailed={handleValidationFailed}
            onEditLead={openEditModal}
            onViewLead={openViewModal}
            onViewActivity={openActivityModal}
            getAssigneeName={getUserDisplayName}
          />
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
      <div className="space-y-3">
        {filteredLeads.length === 0 ? (
          searchQuery || filterScore || filterStatus ? (
            // Filtered empty state - simple message
            <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-12 text-center">
              <div className="w-16 h-16 bg-stone-100 dark:bg-stone-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-stone-400" />
              </div>
              <h3 className="text-lg font-medium text-stone-700 dark:text-stone-300 mb-2">No leads match your filters</h3>
              <p className="text-sm text-stone-500 dark:text-stone-400 mb-4">
                Try adjusting your search or filters to find what you're looking for.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilterScore(null);
                  setFilterStatus(null);
                }}
                className="px-4 py-2 bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 rounded-xl font-medium hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            // Getting started empty state - comprehensive guide
            <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-sage/10 to-sage/5 dark:from-sage/20 dark:to-sage/10 px-8 py-6 border-b border-stone-200 dark:border-stone-800">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-sage/20 rounded-2xl flex items-center justify-center">
                    <Users className="w-7 h-7 text-sage" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-stone-800 dark:text-stone-100">Welcome to Lead Tracking</h3>
                    <p className="text-stone-500 dark:text-stone-400">Turn potential customers into paying studios</p>
                  </div>
                </div>
              </div>

              {/* Steps */}
              <div className="p-8">
                <h4 className="text-sm font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-6">How It Works</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {/* Step 1 */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-xl bg-sage/10 flex items-center justify-center">
                        <Plus className="w-5 h-5 text-sage" />
                      </div>
                    </div>
                    <div>
                      <h5 className="font-medium text-stone-800 dark:text-stone-100 mb-1">1. Capture Leads</h5>
                      <p className="text-sm text-stone-500 dark:text-stone-400">
                        Add potential customers you find on Reddit, Instagram, or through outreach. Record their contact info and pain points.
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-xl bg-sage/10 flex items-center justify-center">
                        <Flame className="w-5 h-5 text-sage" />
                      </div>
                    </div>
                    <div>
                      <h5 className="font-medium text-stone-800 dark:text-stone-100 mb-1">2. Score & Prioritize</h5>
                      <p className="text-sm text-stone-500 dark:text-stone-400">
                        Rate each lead: <span className="text-red-500 font-medium">Hot</span> (ready to buy), <span className="text-amber-500 font-medium">Warm</span> (interested), or <span className="text-blue-500 font-medium">Cold</span> (early stage).
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-xl bg-sage/10 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-sage" />
                      </div>
                    </div>
                    <div>
                      <h5 className="font-medium text-stone-800 dark:text-stone-100 mb-1">3. Track & Follow Up</h5>
                      <p className="text-sm text-stone-500 dark:text-stone-400">
                        Update status as you engage. Set follow-up reminders so no lead falls through the cracks.
                      </p>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-xl bg-sage/10 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-sage" />
                      </div>
                    </div>
                    <div>
                      <h5 className="font-medium text-stone-800 dark:text-stone-100 mb-1">4. Convert to Customer</h5>
                      <p className="text-sm text-stone-500 dark:text-stone-400">
                        Schedule demos, close deals, and mark leads as converted when they sign up!
                      </p>
                    </div>
                  </div>
                </div>

                {/* Status Flow */}
                <div className="bg-stone-50 dark:bg-stone-800/50 rounded-xl p-4 mb-8">
                  <p className="text-xs font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-3">Lead Journey</p>
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="px-3 py-1 rounded-full bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 font-medium">New</span>
                    <ArrowRight className="w-4 h-4 text-stone-400" />
                    <span className="px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium">Contacted</span>
                    <ArrowRight className="w-4 h-4 text-stone-400" />
                    <span className="px-3 py-1 rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 font-medium">Demo Scheduled</span>
                    <ArrowRight className="w-4 h-4 text-stone-400" />
                    <span className="px-3 py-1 rounded-full bg-sage/10 text-sage font-medium">Demo Done</span>
                    <ArrowRight className="w-4 h-4 text-stone-400" />
                    <span className="px-3 py-1 rounded-full bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 font-medium">Converted</span>
                  </div>
                </div>

                {/* CTA */}
                <div className="text-center">
                  <button
                    onClick={() => {
                      resetForm();
                      setShowAddModal(true);
                    }}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-sage text-white rounded-xl font-medium hover:bg-sage/90 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    Add Your First Lead
                  </button>
                  <p className="text-xs text-stone-400 dark:text-stone-500 mt-3">
                    Found a massage studio owner complaining about scheduling? Add them here!
                  </p>
                </div>
              </div>
            </div>
          )
        ) : (
          <>
            {paginatedLeads.map((lead) => {
              const isExpanded = expandedLeadId === lead.id;
              return (
                <div
                  key={lead.id}
                  className={`bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 hover:border-sage/50 transition-all ${isExpanded ? 'ring-2 ring-sage/30' : ''}`}
                >
                  {/* Compact Header - Always Visible */}
                  <div
                    className="flex items-center gap-2 p-3 cursor-pointer"
                    onClick={() => setExpandedLeadId(isExpanded ? null : lead.id)}
                  >
                    {/* Expand/Collapse Icon */}
                    <button className="p-1 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 flex-shrink-0">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>

                    {/* Score Badge */}
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full uppercase flex-shrink-0 ${scoreColors[lead.score]}`}>
                      {lead.score}
                    </span>

                    {/* Name & Studio */}
                    <div className="min-w-0 flex-1">
                      <span className="font-semibold text-stone-800 dark:text-stone-100">{lead.contact_name}</span>
                      {lead.studio_name && (
                        <span className="text-sm text-stone-500 dark:text-stone-400 ml-2">{lead.studio_name}</span>
                      )}
                    </div>

                    {/* Status Badge */}
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${statusColors[lead.status]}`}>
                      {statusLabels[lead.status]}
                    </span>

                    {/* Overdue indicator */}
                    {lead.next_follow_up_date && isOverdue(lead.next_follow_up_date) && (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex-shrink-0">
                        Overdue
                      </span>
                    )}

                    {/* Quick Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => openViewModal(lead)}
                        className="p-1.5 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 transition-colors"
                        title="View Lead"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(lead)}
                        className="p-1.5 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 transition-colors"
                        title="Edit Lead"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-3 pb-3 pt-0 border-t border-stone-100 dark:border-stone-800">
                      <div className="pt-3 space-y-3">
                        {/* Contact Info */}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-stone-500 dark:text-stone-400">
                          {lead.email && (
                            <span className="flex items-center gap-1.5">
                              <Mail className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate max-w-[200px]">{lead.email}</span>
                            </span>
                          )}
                          {lead.phone && (
                            <span className="flex items-center gap-1.5">
                              <Phone className="w-4 h-4 flex-shrink-0" />
                              {lead.phone}
                            </span>
                          )}
                          {(lead.city || lead.state || lead.country) && (
                            <span className="flex items-center gap-1.5">
                              <MapPin className="w-4 h-4 flex-shrink-0" />
                              {[lead.city, lead.state || lead.country].filter(Boolean).join(', ')}
                            </span>
                          )}
                          {lead.source && (
                            <span className="text-xs text-stone-500 dark:text-stone-400">
                              via {sourceLabels[lead.source] || lead.source}
                            </span>
                          )}
                          {lead.assigned_to && getUserDisplayName(lead.assigned_to) && (
                            <span className="flex items-center gap-1 text-xs text-stone-500 dark:text-stone-400">
                              <UserCircle className="w-3 h-3" />
                              {getUserDisplayName(lead.assigned_to)}
                            </span>
                          )}
                        </div>

                        {/* Demo info */}
                        {lead.demo_date && (
                          <div className="flex items-center gap-1.5 text-sm text-sage font-medium">
                            <Calendar className="w-4 h-4 flex-shrink-0" />
                            Demo: {lead.demo_date} {lead.demo_time}
                          </div>
                        )}

                        {/* Pain point and notes */}
                        {lead.pain_point && (
                          <p className="text-sm text-stone-600 dark:text-stone-400">
                            <span className="font-medium">Pain point:</span> {lead.pain_point}
                          </p>
                        )}
                        {lead.notes && (
                          <p className="text-sm text-stone-500 dark:text-stone-500 italic">
                            {lead.notes}
                          </p>
                        )}

                        {/* Action buttons */}
                        <div className="flex items-center gap-2 pt-2">
                          <button
                            onClick={() => openActivityModal(lead)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors"
                          >
                            <History className="w-3.5 h-3.5" />
                            Activity
                          </button>
                          <div className="relative" ref={openMenuId === lead.id ? menuRef : null}>
                            <button
                              onClick={() => setOpenMenuId(openMenuId === lead.id ? null : lead.id)}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors"
                            >
                              <MoreVertical className="w-3.5 h-3.5" />
                              More
                            </button>
                            {openMenuId === lead.id && (
                              <div className="absolute left-0 bottom-full mb-2 w-48 bg-white dark:bg-stone-800 rounded-lg shadow-lg border border-stone-200 dark:border-stone-700 py-1 z-10">
                                <p className="px-3 py-1 text-xs font-medium text-stone-400 uppercase">Change Status</p>
                                {Object.entries(statusLabels).map(([value, label]) => (
                                  <button
                                    key={value}
                                    onClick={() => handleQuickStatusChange(lead, value)}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-stone-50 dark:hover:bg-stone-700 ${lead.status === value ? 'text-sage font-medium' : 'text-stone-700 dark:text-stone-300'}`}
                                  >
                                    {label} {lead.status === value && '✓'}
                                  </button>
                                ))}
                                {isAdmin && (
                                  <>
                                    <div className="border-t border-stone-200 dark:border-stone-700 my-1" />
                                    <button
                                      onClick={() => {
                                        setOpenMenuId(null);
                                        setDeleteConfirmLead(lead);
                                      }}
                                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                      Delete Lead
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-stone-200 dark:border-stone-800">
                <p className="text-sm text-stone-500 dark:text-stone-400">
                  Showing {((currentPage - 1) * leadsPerPage) + 1}-{Math.min(currentPage * leadsPerPage, filteredLeads.length)} of {filteredLeads.length} leads
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm font-medium text-stone-700 dark:text-stone-300 px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      )}

      {/* Add/Edit Lead Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-stone-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white dark:bg-stone-900 px-6 py-4 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-stone-800 dark:text-stone-100">
                {showEditModal ? 'Edit Lead' : 'Add New Lead'}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  setEditingLead(null);
                  resetForm();
                }}
                className="p-1 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Contact Info */}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wide text-stone-900 dark:text-white mb-3 pb-2 border-b-2 border-sage">Contact Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 dark:text-white mb-1.5">
                      Contact Name *
                    </label>
                    <input
                      type="text"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
                      placeholder="Sarah Johnson"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 dark:text-white mb-1.5">
                      Studio Name
                    </label>
                    <input
                      type="text"
                      value={formData.studio_name}
                      onChange={(e) => setFormData({ ...formData, studio_name: e.target.value })}
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
                      placeholder="Tranquil Touch Spa"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 dark:text-white mb-1.5">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
                      placeholder="sarah@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 dark:text-white mb-1.5">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
                      placeholder="512-555-1234"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-stone-900 dark:text-white mb-1.5">
                      Address
                    </label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
                      placeholder="123 Main Street"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 dark:text-white mb-1.5">
                      City
                    </label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
                      placeholder="Austin"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 dark:text-white mb-1.5">
                      State (optional)
                    </label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
                      placeholder="TX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 dark:text-white mb-1.5">
                      Country
                    </label>
                    <input
                      type="text"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
                      placeholder="USA"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 dark:text-white mb-1.5">
                      Source
                    </label>
                    <select
                      value={formData.source}
                      onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
                    >
                      <option value="">Select source...</option>
                      <option value="reddit">Reddit</option>
                      <option value="instagram">Instagram</option>
                      <option value="google">Google</option>
                      <option value="referral">Referral</option>
                      <option value="cold_outreach">Cold Outreach</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Qualification */}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wide text-stone-900 dark:text-white mb-3 pb-2 border-b-2 border-sage">Qualification</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 dark:text-white mb-1.5">
                      Pain Point
                    </label>
                    <input
                      type="text"
                      value={formData.pain_point}
                      onChange={(e) => setFormData({ ...formData, pain_point: e.target.value })}
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
                      placeholder="Double-booking rooms"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 dark:text-white mb-1.5">
                      Current Software
                    </label>
                    <input
                      type="text"
                      value={formData.current_software}
                      onChange={(e) => setFormData({ ...formData, current_software: e.target.value })}
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
                      placeholder="Google Calendar"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_qualified}
                        onChange={(e) => setFormData({ ...formData, is_qualified: e.target.checked })}
                        className="w-4 h-4 rounded border-stone-300 text-sage focus:ring-sage"
                      />
                      <span className="text-sm text-stone-700 dark:text-stone-300">Qualified</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_decision_maker}
                        onChange={(e) => setFormData({ ...formData, is_decision_maker: e.target.checked })}
                        className="w-4 h-4 rounded border-stone-300 text-sage focus:ring-sage"
                      />
                      <span className="text-sm text-stone-700 dark:text-stone-300">Decision Maker</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Lead Score & Status */}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wide text-stone-900 dark:text-white mb-3 pb-2 border-b-2 border-sage">Lead Score & Status</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 dark:text-white mb-1.5">
                      Score
                    </label>
                    <select
                      value={formData.score}
                      onChange={(e) => setFormData({ ...formData, score: e.target.value as 'hot' | 'warm' | 'cold' })}
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
                    >
                      <option value="hot">Hot</option>
                      <option value="warm">Warm</option>
                      <option value="cold">Cold</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 dark:text-white mb-1.5">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as Lead['status'] })}
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="demo_scheduled">Demo Scheduled</option>
                      <option value="demo_done">Demo Done</option>
                      <option value="converted">Converted</option>
                      <option value="lost">Lost</option>
                    </select>
                  </div>
                  {isAdmin && (
                    <div>
                      <label className="block text-sm font-semibold text-stone-900 dark:text-white mb-1.5">
                        Assigned To
                      </label>
                      <select
                        value={formData.assigned_to}
                        onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                        className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
                      >
                        <option value="">Unassigned</option>
                        {marketingUsers.map(mu => (
                          <option key={mu.id} value={mu.id}>
                            {mu.firstName || mu.lastName
                              ? `${mu.firstName || ''} ${mu.lastName || ''}`.trim()
                              : mu.email}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Demo Info */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-bold uppercase tracking-wide text-stone-900 dark:text-white">Demo & Follow-up</h4>
                  {formData.demo_date && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const tempLead = {
                            ...editingLead,
                            demo_date: formData.demo_date,
                            demo_time: formData.demo_time,
                            contact_name: formData.contact_name,
                            studio_name: formData.studio_name,
                            email: formData.email,
                            phone: formData.phone,
                            pain_point: formData.pain_point,
                            notes: formData.notes,
                          } as Lead;
                          generateICSFile(tempLead);
                        }}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg transition-colors"
                        title="Download .ics file"
                      >
                        <CalendarPlus className="w-3.5 h-3.5" />
                        .ics
                      </button>
                      <a
                        href={generateGoogleCalendarUrl({
                          ...editingLead,
                          demo_date: formData.demo_date,
                          demo_time: formData.demo_time,
                          contact_name: formData.contact_name,
                          studio_name: formData.studio_name,
                          email: formData.email,
                          phone: formData.phone,
                          pain_point: formData.pain_point,
                        } as Lead)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg transition-colors"
                        title="Add to Google Calendar"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Google
                      </a>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 dark:text-white mb-1.5">
                      Demo Date
                    </label>
                    <input
                      type="date"
                      value={formData.demo_date}
                      onChange={(e) => setFormData({ ...formData, demo_date: e.target.value, demo_scheduled: Boolean(e.target.value) })}
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 dark:text-white mb-1.5">
                      Demo Time
                    </label>
                    <input
                      type="text"
                      value={formData.demo_time}
                      onChange={(e) => setFormData({ ...formData, demo_time: e.target.value })}
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
                      placeholder="2:00 PM"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 dark:text-white mb-1.5">
                      Next Follow-up
                    </label>
                    <input
                      type="date"
                      value={formData.next_follow_up_date}
                      onChange={(e) => setFormData({ ...formData, next_follow_up_date: e.target.value })}
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-stone-900 dark:text-white mb-1.5">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50 resize-none"
                  placeholder="Additional notes about this lead..."
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-white dark:bg-stone-900 px-6 py-4 border-t border-stone-200 dark:border-stone-800 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  setEditingLead(null);
                  resetForm();
                }}
                disabled={saving}
                className="px-4 py-2 rounded-lg border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={showEditModal ? handleEditLead : handleAddLead}
                disabled={saving || !formData.contact_name.trim()}
                className="px-4 py-2 rounded-lg bg-sage text-white hover:bg-sage/90 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  showEditModal ? 'Update Lead' : 'Add Lead'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-stone-900 rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-stone-800 dark:text-stone-100">
                Delete Lead
              </h3>
              <button
                onClick={() => setDeleteConfirmLead(null)}
                className="p-1 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-stone-600 dark:text-stone-400 mb-6">
              Are you sure you want to delete <strong>{deleteConfirmLead.contact_name}</strong>
              {deleteConfirmLead.studio_name && ` from ${deleteConfirmLead.studio_name}`}?
              This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmLead(null)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteLead(deleteConfirmLead)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Preview Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white dark:bg-stone-900 rounded-2xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden shadow-xl flex flex-col">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-stone-800 dark:text-stone-100">
                  Import Preview
                </h3>
                <p className="text-xs sm:text-sm text-stone-500 dark:text-stone-400">
                  {importPreview.filter(r => r.isValid).length} of {importPreview.length} ready
                </p>
              </div>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportPreview([]);
                }}
                className="p-1.5 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-3 sm:p-6">
              {importPreview.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  No data found in the CSV file
                </div>
              ) : (
                <div className="space-y-2">
                  {importPreview.map((row, index) => (
                    <div
                      key={index}
                      className={`p-2.5 sm:p-3 rounded-lg border ${
                        row.isValid
                          ? 'border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800'
                          : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 sm:gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 sm:gap-2 mb-1 flex-wrap">
                            <span className="font-medium text-sm sm:text-base text-stone-800 dark:text-stone-100">
                              {row.contact_name || '(No name)'}
                            </span>
                            {row.studio_name && (
                              <span className="text-xs sm:text-sm text-stone-500 dark:text-stone-400 truncate max-w-[120px] sm:max-w-none">
                                - {row.studio_name}
                              </span>
                            )}
                            {row.score && (
                              <span className={`text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded-full uppercase ${scoreColors[row.score as keyof typeof scoreColors] || ''}`}>
                                {row.score}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-2 sm:gap-x-3 gap-y-1 text-xs sm:text-sm text-stone-500 dark:text-stone-400">
                            {row.email && <span className="truncate max-w-[150px] sm:max-w-none">{row.email}</span>}
                            {row.phone && <span>{row.phone}</span>}
                            {row.location && <span className="truncate max-w-[100px] sm:max-w-none">{row.location}</span>}
                          </div>
                          {!row.isValid && (
                            <div className="mt-1.5 sm:mt-2 flex flex-wrap gap-1">
                              {row.errors.map((err, i) => (
                                <span key={i} className="text-xs px-1.5 sm:px-2 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                                  {err}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          {row.isValid ? (
                            <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                          ) : (
                            <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-stone-200 dark:border-stone-800 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <div className="text-xs sm:text-sm text-stone-500 text-center sm:text-left">
                <span className="font-medium text-green-600">{importPreview.filter(r => r.isValid).length}</span> valid,{' '}
                <span className="font-medium text-red-600">{importPreview.filter(r => !r.isValid).length}</span> with errors
              </div>
              <div className="flex gap-2 sm:gap-3">
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportPreview([]);
                  }}
                  disabled={importing}
                  className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 rounded-lg border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImportLeads}
                  disabled={importing || importPreview.filter(r => r.isValid).length === 0}
                  className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 rounded-lg bg-sage text-white hover:bg-sage/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="hidden sm:inline">Importing...</span>
                      <span className="sm:hidden">...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      <span className="hidden sm:inline">Import {importPreview.filter(r => r.isValid).length} Leads</span>
                      <span className="sm:hidden">Import ({importPreview.filter(r => r.isValid).length})</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lead View Modal */}
      {showViewModal && viewingLead && (
        <LeadViewModal
          lead={viewingLead}
          onClose={() => {
            setShowViewModal(false);
            setViewingLead(null);
          }}
          onEdit={(lead) => {
            setShowViewModal(false);
            setViewingLead(null);
            openEditModal(lead);
          }}
          getAssigneeName={getUserDisplayName}
        />
      )}

      {/* Activity Timeline Modal */}
      {showActivityModal && activityLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-stone-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-stone-800 dark:text-stone-100">
                  Activity Timeline
                </h3>
                <p className="text-sm text-stone-500 dark:text-stone-400">
                  {activityLead.contact_name} {activityLead.studio_name && `- ${activityLead.studio_name}`}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowActivityModal(false);
                  setActivityLead(null);
                  setActivities([]);
                }}
                className="p-1 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Add Activity Form */}
            <div className="px-4 sm:px-6 py-4 border-b border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-800/50">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <div className="flex gap-2 sm:gap-3">
                  <select
                    value={newActivityType}
                    onChange={(e) => setNewActivityType(e.target.value as 'note' | 'call' | 'email' | 'meeting')}
                    className="px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50 text-sm sm:text-base"
                  >
                    <option value="note">Note</option>
                    <option value="call">Call</option>
                    <option value="email">Email</option>
                    <option value="meeting">Meeting</option>
                  </select>
                  <input
                    type="text"
                    value={newActivityContent}
                    onChange={(e) => setNewActivityContent(e.target.value)}
                    placeholder={`Add a ${newActivityType}...`}
                    className="flex-1 min-w-0 px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50 text-sm sm:text-base"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddActivity()}
                  />
                </div>
                <button
                  onClick={handleAddActivity}
                  disabled={addingActivity || !newActivityContent.trim()}
                  className="px-4 py-2 bg-sage text-white rounded-lg font-medium hover:bg-sage/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 sm:w-auto"
                >
                  {addingActivity ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  <span>Add</span>
                </button>
              </div>
            </div>

            {/* Activities List */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              {loadingActivities ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-sage animate-spin" />
                </div>
              ) : activities.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 bg-stone-100 dark:bg-stone-800 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <History className="w-6 h-6 text-stone-400" />
                  </div>
                  <p className="text-stone-500 dark:text-stone-400">No activity yet</p>
                  <p className="text-sm text-stone-400 dark:text-stone-500 mt-1">
                    Add notes, calls, or emails to track your interactions
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {activities.map((activity) => {
                    const config = activityTypeConfig[activity.activity_type] || activityTypeConfig.note;
                    return (
                      <div key={activity.id} className="flex gap-3">
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-8 h-8 ${config.color} rounded-full flex items-center justify-center text-white`}>
                          {config.icon}
                        </div>
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="font-medium text-stone-800 dark:text-stone-100">
                              {config.label}
                            </span>
                            {activity.created_by_name && (
                              <span className="text-sm text-stone-500 dark:text-stone-400">
                                by {activity.created_by_name}
                              </span>
                            )}
                            <span className="text-xs text-stone-400 dark:text-stone-500">
                              {formatActivityDate(activity.created_at)}
                            </span>
                          </div>
                          {/* Activity-specific content */}
                          {activity.activity_type === 'status_change' && (
                            <div className="mt-1">
                              <p className="text-sm text-stone-600 dark:text-stone-400">
                                Changed status from <span className="font-medium">{statusLabels[activity.old_value as keyof typeof statusLabels] || activity.old_value}</span> to <span className="font-medium">{statusLabels[activity.new_value as keyof typeof statusLabels] || activity.new_value}</span>
                              </p>
                              {isOutOfOrderStatusChange(activity.old_value, activity.new_value) && (
                                <div className="flex items-center gap-1.5 mt-1.5 px-2 py-1 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md w-fit">
                                  <Flag className="w-3.5 h-3.5 text-red-500" />
                                  <span className="text-xs font-medium text-red-600 dark:text-red-400">Moved backwards in pipeline</span>
                                </div>
                              )}
                            </div>
                          )}
                          {activity.activity_type === 'score_change' && (
                            <p className="text-sm text-stone-600 dark:text-stone-400 mt-1">
                              Changed score from <span className={`font-medium ${activity.old_value === 'hot' ? 'text-red-500' : activity.old_value === 'warm' ? 'text-amber-500' : 'text-blue-500'}`}>{activity.old_value}</span> to <span className={`font-medium ${activity.new_value === 'hot' ? 'text-red-500' : activity.new_value === 'warm' ? 'text-amber-500' : 'text-blue-500'}`}>{activity.new_value}</span>
                            </p>
                          )}
                          {activity.activity_type === 'assignment_change' && (
                            <p className="text-sm text-stone-600 dark:text-stone-400 mt-1">
                              {activity.old_value ? `Reassigned from ${getUserDisplayName(activity.old_value) || 'someone'} to ${getUserDisplayName(activity.new_value) || 'someone else'}` : `Assigned to ${getUserDisplayName(activity.new_value) || 'a team member'}`}
                            </p>
                          )}
                          {activity.content && (
                            <p className="text-sm text-stone-600 dark:text-stone-400 mt-1">
                              {activity.content}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stage Requirements Modal */}
      {showRequirementsModal && validationLead && validationTarget && validationResult && (
        <StageRequirementsModal
          lead={validationLead}
          targetStatus={validationTarget}
          missingRequirements={validationResult.missingRequirements}
          onClose={() => {
            setShowRequirementsModal(false);
            setValidationLead(null);
            setValidationTarget(null);
            setValidationResult(null);
          }}
          onSaveAndMove={handleSaveAndMove}
          onOpenFullEdit={() => {
            setShowRequirementsModal(false);
            openEditModal(validationLead);
          }}
        />
      )}
    </div>
  );
}
